import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, Observable } from 'rxjs';
import { ButtonDefinition, PickerValue } from '../generic-picker/generic-picker.component';

@Component({
  selector: 'lib-quarter-picker',
  templateUrl: './quarter-picker.component.html',
  styleUrls: ['./quarter-picker.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => QuarterPickerComponent),
      multi: true
    }
  ],
  standalone: false
})
export class QuarterPickerComponent implements OnInit, ControlValueAccessor {
  @Input()
  public minimumValue: PickerValue | null;
  @Input()
  public maximumValue: PickerValue | null;
  @ViewChild('p')
  public quarterPickerPopover: NgbPopover;
  @Input()
  public validQuarters: Map<number, QuarterInfo> | null;

  public val: PickerValue;
  public availableOptions: ButtonDefinition[] = [
    {
      text: 'Q1',
      value: 1,
      isDisabled: () => this._getDisabled(1),
      isValid: (year: number) => !this._getDisabled(1, year)
    },
    {
      text: 'Q2',
      value: 2,
      isDisabled: () => this._getDisabled(2),
      isValid: (year: number) => !this._getDisabled(2, year)
    },
    {
      text: 'Q3',
      value: 3,
      isDisabled: () => this._getDisabled(3),
      isValid: (year: number) => !this._getDisabled(3, year)
    },
    {
      text: 'Q4',
      value: 4,
      isDisabled: () => this._getDisabled(4),
      isValid: (year: number) => !this._getDisabled(4, year)
    }
  ];
  public get buttonText() {
    return `${this.val.year} / Q${this.val.value}`;
  }
  private onChange = (_: any) => {};

  constructor() {}

  ngOnInit(): void {}

  public valueChanged(val: PickerValue) {
    this.val = val;
    this.onChange({ quarter: val.value, year: val.year });
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState(isDisabled: boolean): void {}

  public writeValue(val: { year: number; quarter: number }): void {
    if (val != null) {
      this.val = { value: val.quarter, year: val.year };
    }
  }

  public disableDecrementYear() {
    if (this.validQuarters != null) {
      return !this.validQuarters.has(this.val.year - 1);
    } else {
      return this.minimumValue != null && this.minimumValue.year === this.val.year;
    }
  }
  public disableIncrementYear() {
    if (this.validQuarters != null) {
      return !this.validQuarters.has(this.val.year + 1);
    } else {
      return this.maximumValue != null && this.maximumValue.year === this.val.year;
    }
  }

  private _getDisabled(value: number, year?: number | null) {
    const yearToTest = year ?? this.val.year;
    if (this.validQuarters != null) {
      if (!this.validQuarters.has(yearToTest)) {
        return true;
      } else {
        const quarterInfo = this.validQuarters.get(yearToTest);
        switch (value) {
          case 1:
            return quarterInfo.q1Count === 0;
          case 2:
            return quarterInfo.q2Count === 0;
          case 3:
            return quarterInfo.q3Count === 0;
          case 4:
            return quarterInfo.q4Count === 0;
        }
      }
    }
    if (this.minimumValue != null) {
      const quarter = value;
      if (yearToTest < this.minimumValue.year) return true;
      if (yearToTest === this.minimumValue.year && quarter < this.minimumValue.value) return true;
    }
    if (this.maximumValue != null) {
      const quarter = value;
      if (yearToTest > this.maximumValue.year) return true;
      if (yearToTest === this.maximumValue.year && quarter > this.maximumValue.value) return true;
    }

    return false;
  }
}
export interface QuarterInfo {
  q1Count: number;
  q2Count: number;
  q3Count: number;
  q4Count: number;
}
